import React from 'react';
import { X, AlertTriangle, Trash2 } from 'lucide-react';
import { Button } from './Button';

interface ConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'warning' | 'info';
  loading?: boolean;
}

export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'danger',
  loading = false,
}) => {
  if (!isOpen) return null;

  const getTypeStyles = () => {
    switch (type) {
      case 'danger':
        return {
          gradient: 'from-error-50 to-error-100',
          iconBg: 'bg-error-100',
          iconColor: 'text-error-600',
          confirmButton: 'bg-gradient-to-r from-error-600 to-error-700 hover:from-error-700 hover:to-error-800',
        };
      case 'warning':
        return {
          gradient: 'from-warning-50 to-warning-100',
          iconBg: 'bg-warning-100',
          iconColor: 'text-warning-600',
          confirmButton: 'bg-gradient-to-r from-warning-600 to-warning-700 hover:from-warning-700 hover:to-warning-800',
        };
      case 'info':
        return {
          gradient: 'from-primary-50 to-primary-100',
          iconBg: 'bg-primary-100',
          iconColor: 'text-primary-600',
          confirmButton: 'bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800',
        };
      default:
        return {
          gradient: 'from-error-50 to-error-100',
          iconBg: 'bg-error-100',
          iconColor: 'text-error-600',
          confirmButton: 'bg-gradient-to-r from-error-600 to-error-700 hover:from-error-700 hover:to-error-800',
        };
    }
  };

  const styles = getTypeStyles();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full transform transition-all duration-200 scale-100">
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r ${styles.gradient}`}>
          <div className="flex items-center space-x-3">
            <div className={`w-12 h-12 ${styles.iconBg} rounded-lg flex items-center justify-center`}>
              {type === 'danger' ? (
                <Trash2 className={`w-6 h-6 ${styles.iconColor}`} />
              ) : (
                <AlertTriangle className={`w-6 h-6 ${styles.iconColor}`} />
              )}
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{title}</h2>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200 disabled:opacity-50"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-700 text-center mb-6 leading-relaxed">
            {message}
          </p>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={loading}
              className="min-w-[80px]"
            >
              {cancelText}
            </Button>
            <Button
              type="button"
              onClick={onConfirm}
              loading={loading}
              disabled={loading}
              className={`min-w-[100px] text-white ${styles.confirmButton}`}
            >
              {confirmText}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};